import React, { Component } from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate, useLocation } from 'react-router-dom';
import Login from './components/Login';
import Dashboard from './components/Dashboard';
import TutorDashboard from './components/TutorDashboard';
import ParentPortal from './components/ParentPortal';
import { AuthProvider, useAuth } from './context/AuthContext';
import { DataProvider, useData } from './context/DataContext';
import PaymentVerification from './components/PaymentVerification';
import './App.css';

// React Error Boundary to catch logic crashes
class ErrorBoundary extends Component {
  constructor(props) {
    super(props);
    this.state = { hasError: false, error: null };
  }

  static getDerivedStateFromError(error) {
    return { hasError: true, error };
  }

  render() {
    if (this.state.hasError) {
      return (
        <div className="min-h-screen flex items-center justify-center bg-gray-50 p-10">
          <div className="max-w-md w-full bg-white shadow-2xl rounded-3xl p-8 text-center border-t-8 border-red-500">
            <h1 className="text-2xl font-black text-gray-900 mb-4">Application Restored</h1>
            <p className="text-gray-500 mb-6 text-sm">A component encountered an unexpected error. We've captured the details and are ready to reset.</p>
            <button 
              onClick={() => {
                localStorage.clear();
                window.location.href = '/';
              }}
              className="w-full py-4 bg-gray-900 text-white rounded-2xl font-bold hover:bg-black transition-all"
            >
              Hard Reset & Repair
            </button>
            <p className="mt-4 text-[10px] text-gray-400 font-mono overflow-hidden truncate">
              {this.state.error?.message}
            </p>
          </div>
        </div>
      );
    }
    return this.props.children;
  }
}

function AppContent() {
  const { user, isAuthenticated, loading: authLoading, hasPermission } = useAuth();
  const { dataLoaded } = useData();
  const location = useLocation();

  // 1. Handle Loading State
  if (authLoading || !dataLoaded) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-white">
        <div className="flex flex-col items-center gap-4">
          <div className="w-12 h-12 border-4 border-indigo-600 border-t-transparent rounded-full animate-spin"></div>
          <p className="text-xs font-black text-gray-400 uppercase tracking-widest">Syncing with Cloud...</p>
        </div>
      </div>
    );
  }

  // 2. Handle Payment Callbacks
  const searchParams = new URLSearchParams(location.search);
  if (searchParams.get('payment') === 'success') {
    return <PaymentVerification sessionId={searchParams.get('session_id')} />;
  }

  // 3. Handle Authentication
  if (!isAuthenticated || !user) {
    return <Login />;
  }

  // 4. Role-Based Routing
  return (
    <Routes>
      <Route path="/" element={
        user.role === 'parent' 
          ? <ParentPortal /> 
          : (user.role === 'admin' || hasPermission('canManageStudents')) 
            ? <Dashboard /> 
            : <TutorDashboard />
      } />
      <Route path="*" element={<Navigate to="/" replace />} />
    </Routes>
  );
}

function App() {
  return (
    <ErrorBoundary>
      <DataProvider>
        <AuthProvider>
          <Router>
            <div className="min-h-screen bg-gray-50">
              <AppContent />
            </div>
          </Router>
        </AuthProvider>
      </DataProvider>
    </ErrorBoundary>
  );
}

export default App;